// Prism tokens are the source of truth for new Scalar Design System surfaces.
// Keep legacy `palette.ts` stable until the full app migration is intentional.
export const AI = '#A614FF' as const;

export const NEUTRAL = {
  white: '#FFFFFF',
  black: '#000000',
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E3E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
} as const;

export const NEUTRAL_ALPHA = {
  white: {
    30: 'rgba(255, 255, 255, 0.3)',
    50: 'rgba(255, 255, 255, 0.5)',
    70: 'rgba(255, 255, 255, 0.7)',
  },
  black: {
    30: 'rgba(0, 0, 0, 0.3)',
    50: 'rgba(0, 0, 0, 0.5)',
    70: 'rgba(0, 0, 0, 0.7)',
  },
} as const;

export const BRAND = {
  100: '#CDE5FA',
  200: '#9ACBF6',
  300: '#68B1F1',
  400: '#3597ED',
  500: '#037DE8',
  600: '#0268C1',
  700: '#02539A',
  800: '#013E73',
  900: '#01294C',
} as const;

export const ACCENT = {
  100: '#CEF3E6',
  200: '#9CE8CD',
  300: '#6BDCB3',
  400: '#39D19A',
  500: '#08C581',
  600: '#07A46B',
  700: '#058355',
  800: '#046140',
  900: '#03402A',
} as const;

export const PURPLE = {
  100: '#DFCFFF',
  200: '#C2A8FF',
  300: '#A77FFF',
  400: '#8A58FF',
  500: '#6E2FFF',
  600: '#5826D6',
  700: '#441DA3',
  800: '#2F1571',
  900: '#1A0B3E',
} as const;

export const BLUE = {
  100: '#CED3FD',
  200: '#9EA7FA',
  300: '#6D7BF8',
  400: '#3D4FF5',
  500: '#2A3FE4',
  600: '#0A1CC2',
  700: '#071592',
  800: '#050E61',
  900: '#020731',
} as const;

export const GREEN = {
  100: '#BBE8CE',
  200: '#7ED6A5',
  300: '#31C37B',
  400: '#00B04F',
  500: '#009C1C',
  600: '#007E17',
  700: '#006012',
  800: '#00420F',
  900: '#002409',
} as const;

export const YELLOW = {
  100: '#FFEECC',
  200: '#FFDD99',
  300: '#FFCC66',
  400: '#FFBB33',
  500: '#FFAA00',
  600: '#CC8800',
  700: '#996600',
  800: '#664400',
  900: '#332200',
} as const;

export const ORANGE = {
  100: '#FFE0C4',
  200: '#FFC790',
  300: '#FFAC5C',
  400: '#FF9211',
  500: '#FF7700',
  600: '#D26000',
  700: '#A04900',
  800: '#6F3300',
  900: '#3C1B00',
} as const;

export const RED = {
  100: '#FFC8C9',
  200: '#FF999C',
  300: '#FF686E',
  400: '#FF2F3D',
  500: '#FB0000',
  600: '#CB0000',
  700: '#9C0000',
  800: '#6C0003',
  900: '#3B0104',
} as const;

export const PRIMARY_NAVIGATION_COLORS = {
  mostRecentMeasurementDate: '#D0FDF9',
} as const;

export const PRISM_COLORS = {
  ai: AI,
  neutral: NEUTRAL,
  neutralAlpha: NEUTRAL_ALPHA,
  brand: BRAND,
  accent: ACCENT,
  purple: PURPLE,
  blue: BLUE,
  green: GREEN,
  yellow: YELLOW,
  orange: ORANGE,
  red: RED,
  primaryNavigation: PRIMARY_NAVIGATION_COLORS,
} as const;

export const PRISM_PRIMITIVE_PALETTE = {
  ai: AI,
  neutral: NEUTRAL,
  neutralAlpha: NEUTRAL_ALPHA,
  brand: BRAND,
  accent: ACCENT,
  purple: PURPLE,
  blue: BLUE,
  green: GREEN,
  yellow: YELLOW,
  orange: ORANGE,
  red: RED,
} as const;

const PRISM_FONT_FAMILY = '"Inter", "Open Sans", sans-serif';

export const PRISM_RADIUS = {
  xs: 4,
  s: 8,
  m: 16,
  l: 24,
  xl: 32,
} as const;

export const PRISM_SPACING = {
  xxs: 2,
  xs: 4,
  s: 8,
  m: 16,
  l: 24,
  xl: 32,
} as const;

export const PRISM_BREAKPOINTS = {
  mobile393: 393,
  tablet768: 768,
  tablet1024: 1024,
  desktop1440: 1440,
  desktop1920: 1920,
} as const;

export const PRISM_TYPE = {
  family: {
    main: PRISM_FONT_FAMILY,
  },
  weight: {
    light: 300,
    regular: 400,
    semiBold: 600,
    bold: 700,
  },
  size: {
    xs: 10,
    s: 12,
    m: 14,
    l: 16,
    xl: 18,
    '2xl': 20,
    '3xl': 24,
    '4xl': 28,
    '5xl': 32,
  },
  lineHeight: {
    xs: 14,
    s: 16,
    m: 20,
    l: 24,
    xl: 26,
    '2xl': 28,
    '3xl': 32,
    '4xl': 36,
    '5xl': 40,
  },
  letterSpacing: {
    xs: -1,
    s: -0.5,
    none: 0,
  },
} as const;

// Add theme color aliases only when code consumes them, so this layer stays useful
// instead of mirroring every Figma semantic variable up front.
export const PRISM_SEMANTIC_COLORS = {
  primary: {
    main: BRAND[500],
    hover: BRAND[600],
    pressed: BRAND[700],
    contrast: NEUTRAL.white,
  },
  text: {
    primary: NEUTRAL[900],
    secondary: NEUTRAL[800],
    tertiary: NEUTRAL[600],
    disabled: NEUTRAL[300],
    link: BRAND[800],
  },
  background: {
    page: NEUTRAL[50],
    paper: NEUTRAL.white,
  },
  action: {
    disabled: NEUTRAL[500],
    disabledBackground: NEUTRAL[200],
    focus: BRAND[200],
  },
  stroke: {
    subtle: NEUTRAL[300],
  },
  feedback: {
    error: RED[400],
    info: BRAND[500],
    success: GREEN[400],
    successHover: GREEN[500],
    successPressed: GREEN[600],
    warning: ORANGE[500],
  },
  status: {
    positive: GREEN[400],
    warning: YELLOW[600],
    negative: RED[600],
  },
} as const;

export const PRISM_NAVIGATION_PALETTE = {
  primary: {
    bar: {
      background: BRAND[700],
    },
    tab: {
      default: BRAND[200],
      active: NEUTRAL.white,
      activeUnderline: NEUTRAL.white,
      hover: NEUTRAL.white,
    },
    chip: {
      background: BRAND[900],
      hoverBackground: BRAND[800],
      icon: NEUTRAL[100],
      label: NEUTRAL[400],
      value: {
        default: NEUTRAL[100],
        strong: NEUTRAL.white,
        highlight: PRIMARY_NAVIGATION_COLORS.mostRecentMeasurementDate,
      },
    },
    iconButton: {
      color: NEUTRAL[100],
      hoverBackgroundBase: NEUTRAL.white,
    },
    menu: {
      surface: NEUTRAL.white,
      shadow: NEUTRAL[900],
      search: {
        background: NEUTRAL[200],
        border: NEUTRAL[500],
        text: NEUTRAL[800],
      },
      action: {
        background: NEUTRAL.white,
        border: BRAND[500],
        hoverBackgroundBase: BRAND[500],
        text: BRAND[500],
      },
      item: {
        activeBackground: BRAND[200],
        disabled: NEUTRAL[500],
        header: NEUTRAL[800],
        hoverBackground: NEUTRAL[400],
        primaryText: NEUTRAL[900],
        secondaryText: NEUTRAL[600],
      },
    },
  },
  secondary: {
    bar: {
      background: NEUTRAL.white,
    },
    title: {
      text: NEUTRAL[900],
    },
    statusBadge: {
      draft: {
        background: NEUTRAL[100],
        text: PRISM_SEMANTIC_COLORS.text.tertiary,
      },
      final: {
        background: RED[400],
        text: NEUTRAL.white,
      },
      published: {
        background: BRAND[500],
        text: NEUTRAL.white,
      },
    },
    tab: {
      default: NEUTRAL[500],
      disabled: NEUTRAL[400],
      focusOutline: BRAND[200],
      hoverBackground: NEUTRAL[50],
      selectedBackground: NEUTRAL[100],
      selectedText: NEUTRAL[900],
    },
    infoLabel: {
      background: NEUTRAL.white,
      border: NEUTRAL[200],
      label: NEUTRAL[500],
      value: {
        brand: BRAND[500],
        default: NEUTRAL[900],
        negative: RED[600],
        positive: GREEN[500],
        warning: YELLOW[600],
      },
    },
    actionButton: {
      disabledBackground: NEUTRAL[100],
      disabledBorder: NEUTRAL[200],
      disabledText: NEUTRAL[400],
      ghost: {
        background: 'transparent',
        border: 'transparent',
        hoverBackground: NEUTRAL[50],
        hoverText: NEUTRAL[900],
        text: NEUTRAL[700],
      },
      primary: {
        background: BRAND[500],
        border: BRAND[500],
        hoverBackground: BRAND[600],
        hoverBorder: BRAND[600],
        text: NEUTRAL.white,
      },
      positive: {
        background: GREEN[400],
        border: GREEN[400],
        hoverBackground: GREEN[500],
        hoverBorder: GREEN[500],
        text: NEUTRAL.white,
      },
      secondary: {
        background: NEUTRAL.white,
        border: BRAND[500],
        hoverBackground: BRAND[100],
        hoverBorder: BRAND[600],
        hoverText: BRAND[600],
        text: BRAND[500],
      },
    },
    iconButton: {
      activeBackground: NEUTRAL[100],
      activeText: NEUTRAL[900],
      containedBackground: NEUTRAL[300],
      containedHoverBackground: NEUTRAL[400],
      containedText: NEUTRAL[700],
      defaultText: NEUTRAL[600],
      disabledText: NEUTRAL[400],
      hoverBackground: NEUTRAL[50],
      hoverText: NEUTRAL[900],
    },
    selector: {
      background: NEUTRAL.white,
      border: NEUTRAL[200],
      disabledBackground: NEUTRAL[100],
      disabledBorder: NEUTRAL[200],
      disabledText: NEUTRAL[400],
      filterBackground: NEUTRAL[300],
      filterHoverBackground: NEUTRAL[400],
      filterText: NEUTRAL[700],
      focusOutline: BRAND[300],
      hoverBackground: NEUTRAL[50],
      hoverBorder: NEUTRAL[300],
      hoverText: BRAND[600],
      icon: NEUTRAL[500],
      label: NEUTRAL[500],
      value: BRAND[500],
    },
  },
  tertiary: {
    bar: {
      background: NEUTRAL[100],
    },
    item: {
      defaultText: NEUTRAL[500],
      disabledText: NEUTRAL[400],
      focusOutline: BRAND[300],
      hoverBackground: NEUTRAL[200],
      hoverText: NEUTRAL[900],
      menuIcon: NEUTRAL[600],
      selectedBackground: NEUTRAL[300],
      selectedText: NEUTRAL[900],
    },
    iconButton: {
      background: NEUTRAL[100],
      color: NEUTRAL[700],
      disabledColor: NEUTRAL[400],
      hoverBackground: NEUTRAL[200],
      hoverColor: NEUTRAL[900],
      menuBackground: 'transparent',
    },
    aiButton: {
      background: `linear-gradient(90deg, ${AI} 47.98%, ${BLUE[500]} 102.71%)`,
      hoverBackground: `linear-gradient(90deg, ${PURPLE[600]} 47.98%, ${BLUE[600]} 102.71%)`,
      text: NEUTRAL.white,
    },
    currencySelector: {
      background: NEUTRAL.white,
      border: NEUTRAL[200],
      hoverBackground: NEUTRAL[50],
      unitBackground: NEUTRAL[200],
      unitHoverBackground: NEUTRAL[300],
      unitText: NEUTRAL[800],
      valueText: GREEN[500],
    },
    actionButton: {
      positive: {
        background: GREEN[400],
        border: GREEN[400],
        hoverBackground: GREEN[500],
        hoverBorder: GREEN[500],
        text: NEUTRAL.white,
      },
      primary: {
        background: BRAND[500],
        border: BRAND[500],
        hoverBackground: BRAND[600],
        hoverBorder: BRAND[600],
        text: NEUTRAL.white,
      },
      secondary: {
        background: 'transparent',
        border: BRAND[500],
        hoverBackground: BRAND[100],
        hoverBorder: BRAND[600],
        hoverText: BRAND[600],
        text: BRAND[500],
      },
    },
  },
} as const;

export const PRISM_TOKENS = {
  colors: PRISM_COLORS,
  primitivePalette: PRISM_PRIMITIVE_PALETTE,
  radius: PRISM_RADIUS,
  spacing: PRISM_SPACING,
  breakpoints: PRISM_BREAKPOINTS,
  type: PRISM_TYPE,
  themeColors: PRISM_SEMANTIC_COLORS,
  navigation: PRISM_NAVIGATION_PALETTE,
} as const;
